import React, { useState, useMemo, useCallback } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';
import DictFieldConfigPanel, { isDictConfigComplete } from './DictFieldConfigPanel';
import FkCompanionConfigPanel, { isFkConfigComplete } from './FkCompanionConfigPanel';
import type { ColumnSuggestion, FkFieldMapping } from '../../services/data-integration-api';

interface MappingEditorProps {
  sourceColumns: Array<{ column_name: string; data_type: string; is_primary_key: boolean; column_comment: string; column_key_type?: string | null }>;
  suggestedMappings: ColumnSuggestion[];
  normalizedTableName: string;
  syncIdentityColumn: string;
  onTableNameChange: (name: string) => void;
  onSyncIdentityChange: (col: string) => void;
  onMappingsChange: (mappings: ColumnSuggestion[]) => void;
  catalogTables?: Array<{ table_name: string; table_comment: string }>;
  sourceConnectionId?: string;
}

export default function MappingEditor({
  sourceColumns,
  suggestedMappings,
  normalizedTableName,
  syncIdentityColumn,
  onTableNameChange,
  onSyncIdentityChange,
  onMappingsChange,
  catalogTables = [],
  sourceConnectionId = '',
}: MappingEditorProps) {
  const [mappings, setMappings] = useState<ColumnSuggestion[]>(suggestedMappings);
  const [expandedDictRows, setExpandedDictRows] = useState<Set<string>>(new Set());
  const [expandedFkRows, setExpandedFkRows] = useState<Set<string>>(new Set());

  const mappingBySource = useMemo(() => {
    const map = new Map<string, { mapping: ColumnSuggestion; index: number }>();
    mappings.forEach((m, idx) => {
      if (m.source_column_name) {
        map.set(m.source_column_name, { mapping: m, index: idx });
      }
    });
    return map;
  }, [mappings]);

  // 孤儿映射：source_column_name 不在 sourceColumns 中（排除 FK 伴生列）
  const orphanMappings = useMemo(() => {
    const sourceNameSet = new Set(sourceColumns.map(c => c.column_name));
    return mappings
      .map((m, idx) => ({ mapping: m, index: idx }))
      .filter(({ mapping }) =>
        mapping.source_column_name &&
        !sourceNameSet.has(mapping.source_column_name) &&
        !mapping.fk_companion_of
      );
  }, [mappings, sourceColumns]);

  // FK 伴生列按父列分组
  const companionsByParent = useMemo(() => {
    const map = new Map<string, Array<{ mapping: ColumnSuggestion; index: number }>>();
    mappings.forEach((m, idx) => {
      if (m.fk_companion_of) {
        const list = map.get(m.fk_companion_of) || [];
        list.push({ mapping: m, index: idx });
        map.set(m.fk_companion_of, list);
      }
    });
    return map;
  }, [mappings]);

  const doUpdate = useCallback((updated: ColumnSuggestion[]) => {
    setMappings(updated);
    onMappingsChange(updated);
  }, [onMappingsChange]);

  const toggleDictExpand = (key: string) => {
    setExpandedDictRows(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleFkExpand = (key: string) => {
    setExpandedFkRows(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const updateMapping = (mappingIndex: number, field: string, value: any) => {
    const updated = [...mappings];
    (updated[mappingIndex] as any)[field] = value;
    updated[mappingIndex].mapping_status = 'manual';
    doUpdate(updated);
  };

  const resetToSuggested = () => {
    setMappings([...suggestedMappings]);
    onMappingsChange(suggestedMappings);
    setExpandedDictRows(new Set());
    setExpandedFkRows(new Set());
  };

  /** 启用 FK 标定 */
  const enableFk = (mappingIndex: number, fkType: 'person' | 'department', rowKey: string) => {
    const updated = [...mappings];
    const m = updated[mappingIndex];

    // 互斥：如果已标定为字典，先清除
    if (m.is_dict_field) {
      m.is_dict_field = false;
      m.dict_table_name = '';
      m.dict_type_column = '';
      m.dict_type_value = '';
      m.dict_code_column = '';
      m.dict_value_column = '';
      m.dict_companion_column_name = '';
      setExpandedDictRows(prev => {
        const next = new Set(prev);
        next.delete(rowKey);
        return next;
      });
      toast.info('已清除字典配置（外键与字典互斥）');
    }

    m.is_fk_field = true;
    m.fk_type = fkType;
    m.fk_field_mappings = [];
    m.mapping_status = 'manual';
    doUpdate(updated);
    setExpandedFkRows(prev => new Set(prev).add(rowKey));
  };

  /** 取消 FK 标定 */
  const disableFk = (mappingIndex: number, rowKey: string) => {
    const updated = [...mappings];
    const m = updated[mappingIndex];
    const sourceCol = m.source_column_name;

    // 查找伴生列
    const companions = updated.filter(c => c.fk_companion_of === sourceCol);
    const hasManualEdit = companions.some(c => c.mapping_status === 'manual');

    if (hasManualEdit) {
      if (!window.confirm('部分 FK 伴生列已被手动修改，确定要移除吗？')) {
        return;
      }
    }

    // 移除伴生列
    const filtered = updated.filter(c => c.fk_companion_of !== sourceCol);

    // 清除 FK 标记
    const parentIdx = filtered.indexOf(m);
    if (parentIdx >= 0) {
      filtered[parentIdx] = {
        ...filtered[parentIdx],
        is_fk_field: false,
        fk_type: undefined,
        fk_ref_table: '',
        fk_ref_pk_column: '',
        fk_field_mappings: [],
        mapping_status: 'manual',
      };
    }

    setExpandedFkRows(prev => {
      const next = new Set(prev);
      next.delete(rowKey);
      return next;
    });
    doUpdate(filtered);
  };

  /** FK 配置变更回调 */
  const handleFkConfigChange = (mappingIndex: number, field: string, value: any) => {
    updateMapping(mappingIndex, field, value);
  };

  /** FK 伴生列变更回调 */
  const handleCompanionColumnsChange = (parentSourceColumnName: string, companions: ColumnSuggestion[]) => {
    // 移除旧的伴生列
    const updated = mappings.filter(m => m.fk_companion_of !== parentSourceColumnName);
    // 找到父列位置
    const parentIdx = updated.findIndex(m => m.source_column_name === parentSourceColumnName);
    // 在父列之后插入新的伴生列
    if (parentIdx >= 0) {
      updated.splice(parentIdx + 1, 0, ...companions);
    } else {
      updated.push(...companions);
    }
    doUpdate(updated);
  };

  /** 启用字典标定（含互斥检查） */
  const enableDict = (mappingIndex: number, rowKey: string) => {
    const updated = [...mappings];
    const m = updated[mappingIndex];

    // 互斥：如果已标定为 FK，先清除
    if (m.is_fk_field) {
      const sourceCol = m.source_column_name;
      // 移除伴生列
      const filtered = updated.filter(c => c.fk_companion_of !== sourceCol);
      const parentIdx = filtered.findIndex(c => c.source_column_name === sourceCol);
      if (parentIdx >= 0) {
        filtered[parentIdx] = {
          ...filtered[parentIdx],
          is_fk_field: false,
          fk_type: undefined,
          fk_ref_table: '',
          fk_ref_pk_column: '',
          fk_field_mappings: [],
        };
      }
      setExpandedFkRows(prev => {
        const next = new Set(prev);
        next.delete(rowKey);
        return next;
      });
      toast.info('已清除外键配置（字典与外键互斥）');
      // 启用字典
      if (parentIdx >= 0) {
        filtered[parentIdx].is_dict_field = true;
        filtered[parentIdx].mapping_status = 'manual';
        if (!filtered[parentIdx].dict_companion_column_name) {
          filtered[parentIdx].dict_companion_column_name = `${filtered[parentIdx].target_column_name}_text`;
        }
      }
      setExpandedDictRows(prev => new Set(prev).add(rowKey));
      doUpdate(filtered);
      return;
    }

    m.is_dict_field = true;
    m.mapping_status = 'manual';
    if (!m.dict_companion_column_name) {
      m.dict_companion_column_name = `${m.target_column_name}_text`;
    }
    setExpandedDictRows(prev => new Set(prev).add(rowKey));
    doUpdate(updated);
  };

  /** 禁用字典标定 */
  const disableDict = (mappingIndex: number, rowKey: string) => {
    updateMapping(mappingIndex, 'is_dict_field', false);
    setExpandedDictRows(prev => {
      const next = new Set(prev);
      next.delete(rowKey);
      return next;
    });
  };

  /** 渲染目标字段可编辑列 */
  const renderTargetColumns = (m: ColumnSuggestion, mappingIndex: number, rowKey: string, isCompanion = false) => (
    <>
      <TableCell className="p-1">
        <Input
          type="text"
          value={m.target_column_name}
          onChange={e => updateMapping(mappingIndex, 'target_column_name', e.target.value)}
          className="h-8 font-mono"
          readOnly={isCompanion}
        />
      </TableCell>
      <TableCell className="p-1">
        <select
          value={m.target_data_type}
          onChange={e => updateMapping(mappingIndex, 'target_data_type', e.target.value)}
          className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm"
          disabled={isCompanion}
        >
          {['varchar', 'integer', 'bigint', 'smallint', 'boolean', 'decimal',
            'float', 'text', 'date', 'timestamp', 'json', 'jsonb'].map(t => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </TableCell>
      <TableCell className="p-1">
        <Input
          type="text"
          value={m.target_column_comment || ''}
          onChange={e => updateMapping(mappingIndex, 'target_column_comment', e.target.value)}
          className="h-8 w-28"
          placeholder="字段注释"
          readOnly={isCompanion}
        />
      </TableCell>
      <TableCell className="p-1">
        <Input
          type="number"
          value={m.target_column_length || ''}
          onChange={e => updateMapping(mappingIndex, 'target_column_length', e.target.value ? Number(e.target.value) : null)}
          className="h-8 w-16"
          readOnly={isCompanion}
        />
      </TableCell>
      <TableCell className="p-1 text-center">
        <input
          type="checkbox"
          checked={m.target_is_nullable !== false}
          onChange={e => updateMapping(mappingIndex, 'target_is_nullable', e.target.checked)}
          disabled={isCompanion}
        />
      </TableCell>
      <TableCell className="p-1 text-center">
        <input
          type="checkbox"
          checked={!!m.target_is_index}
          onChange={e => updateMapping(mappingIndex, 'target_is_index', e.target.checked)}
          disabled={isCompanion}
        />
      </TableCell>
      {/* 字典列 */}
      <TableCell className="p-1 text-center">
        {isCompanion ? (
          <span className="text-muted-foreground text-xs">—</span>
        ) : (
          <div className="flex items-center justify-center gap-1">
            <input
              type="checkbox"
              checked={!!m.is_dict_field}
              onChange={e => {
                if (e.target.checked) {
                  enableDict(mappingIndex, rowKey);
                } else {
                  disableDict(mappingIndex, rowKey);
                }
              }}
            />
            {m.is_dict_field && (
              <>
                <span className={`inline-block h-2 w-2 rounded-full ${
                  isDictConfigComplete({
                    dict_table_name: m.dict_table_name || '',
                    dict_code_column: m.dict_code_column || '',
                    dict_value_column: m.dict_value_column || '',
                    dict_companion_column_name: m.dict_companion_column_name || '',
                  })
                    ? 'bg-[var(--color-success)]'
                    : 'bg-[var(--color-warning)]'
                }`} />
                <button
                  type="button"
                  onClick={() => toggleDictExpand(rowKey)}
                  className="p-0.5 hover:bg-accent rounded"
                >
                  {expandedDictRows.has(rowKey) ? (
                    <ChevronDown className="h-3 w-3" />
                  ) : (
                    <ChevronRight className="h-3 w-3" />
                  )}
                </button>
              </>
            )}
          </div>
        )}
      </TableCell>
      {/* 外键列 */}
      <TableCell className="p-1 text-center">
        {isCompanion ? (
          <Badge variant="outline" className="text-xs text-[var(--color-accent-blue)]">
            FK 伴生
          </Badge>
        ) : (
          <div className="flex items-center justify-center gap-1">
            <input
              type="checkbox"
              checked={!!m.is_fk_field}
              onChange={e => {
                if (e.target.checked) {
                  enableFk(mappingIndex, 'person', rowKey);
                } else {
                  disableFk(mappingIndex, rowKey);
                }
              }}
            />
            {m.is_fk_field && (
              <>
                <select
                  value={m.fk_type || 'person'}
                  onChange={e => {
                    const newType = e.target.value as 'person' | 'department';
                    // 切换类型时重置配置
                    const updated = [...mappings];
                    const um = updated[mappingIndex];
                    // 移除旧伴生列
                    const filtered = updated.filter(c => c.fk_companion_of !== um.source_column_name);
                    const parentIdx = filtered.findIndex(c => c.source_column_name === um.source_column_name);
                    if (parentIdx >= 0) {
                      filtered[parentIdx] = {
                        ...filtered[parentIdx],
                        fk_type: newType,
                        fk_ref_table: '',
                        fk_ref_pk_column: '',
                        fk_field_mappings: [],
                      };
                    }
                    doUpdate(filtered);
                  }}
                  className="h-6 rounded border border-input bg-background px-1 text-xs"
                >
                  <option value="person">人员</option>
                  <option value="department">部门</option>
                </select>
                <span className={`inline-block h-2 w-2 rounded-full ${
                  isFkConfigComplete({
                    fk_ref_table: m.fk_ref_table || '',
                    fk_ref_pk_column: m.fk_ref_pk_column || '',
                    fk_field_mappings: m.fk_field_mappings || [],
                  })
                    ? 'bg-[var(--color-success)]'
                    : 'bg-[var(--color-warning)]'
                }`} />
                <button
                  type="button"
                  onClick={() => toggleFkExpand(rowKey)}
                  className="p-0.5 hover:bg-accent rounded"
                >
                  {expandedFkRows.has(rowKey) ? (
                    <ChevronDown className="h-3 w-3" />
                  ) : (
                    <ChevronRight className="h-3 w-3" />
                  )}
                </button>
              </>
            )}
          </div>
        )}
      </TableCell>
      <TableCell className="p-1">
        <Badge className={
          m.mapping_status === 'auto'
            ? 'bg-[var(--color-success-light)] text-[var(--color-success)]'
            : 'bg-[var(--color-warning-light)] text-[var(--color-warning)]'
        }>
          {m.mapping_status}
        </Badge>
      </TableCell>
      <TableCell className="p-1">
        {m.suggestion_reason ? (
          <Badge className="bg-[var(--color-accent-blue)] text-white text-xs max-w-[160px] truncate" title={m.suggestion_reason}>
            {m.suggestion_reason}
          </Badge>
        ) : null}
      </TableCell>
    </>
  );

  /** 渲染字典配置面板展开行 */
  const renderDictPanel = (m: ColumnSuggestion, mappingIndex: number, rowKey: string) => {
    if (!m.is_dict_field || !expandedDictRows.has(rowKey)) return null;
    return (
      <TableRow>
        <TableCell colSpan={14} className="p-2 bg-muted/30">
          <DictFieldConfigPanel
            targetColumnName={m.target_column_name}
            sourceConnectionId={sourceConnectionId}
            catalogTables={catalogTables}
            config={{
              dict_table_name: m.dict_table_name || '',
              dict_type_column: m.dict_type_column || '',
              dict_type_value: m.dict_type_value || '',
              dict_code_column: m.dict_code_column || '',
              dict_value_column: m.dict_value_column || '',
              dict_companion_column_name: m.dict_companion_column_name || '',
            }}
            onChange={(field, value) => updateMapping(mappingIndex, field, value)}
          />
        </TableCell>
      </TableRow>
    );
  };

  /** 渲染 FK 配置面板展开行 */
  const renderFkPanel = (m: ColumnSuggestion, mappingIndex: number, rowKey: string) => {
    if (!m.is_fk_field || !expandedFkRows.has(rowKey)) return null;
    return (
      <TableRow>
        <TableCell colSpan={14} className="p-2 bg-muted/30">
          <FkCompanionConfigPanel
            targetColumnName={m.target_column_name}
            sourceColumnName={m.source_column_name}
            sourceConnectionId={sourceConnectionId}
            catalogTables={catalogTables}
            fkType={m.fk_type || 'person'}
            config={{
              fk_ref_table: m.fk_ref_table || '',
              fk_ref_pk_column: m.fk_ref_pk_column || '',
              fk_field_mappings: m.fk_field_mappings || [],
            }}
            onChange={(field, value) => handleFkConfigChange(mappingIndex, field, value)}
            onCompanionColumnsChange={(companions) => handleCompanionColumnsChange(m.source_column_name, companions)}
          />
        </TableCell>
      </TableRow>
    );
  };

  return (
    <div>
      <div className="mb-4 flex items-end gap-4">
        <div className="space-y-1.5">
          <Label>目标表名</Label>
          <Input
            type="text"
            value={normalizedTableName}
            onChange={e => onTableNameChange(e.target.value)}
            className="font-mono w-[300px]"
          />
        </div>
        <div className="space-y-1.5">
          <Label>同步标识列</Label>
          <Input
            type="text"
            value={syncIdentityColumn}
            onChange={e => onSyncIdentityChange(e.target.value)}
            className="font-mono w-[200px]"
          />
        </div>
        <Button variant="outline" onClick={resetToSuggested}>重置为建议</Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            {/* 源表字段（只读） */}
            <TableHead className="bg-muted/40">字段名</TableHead>
            <TableHead className="bg-muted/40">类型</TableHead>
            <TableHead className="bg-muted/40">PK</TableHead>
            <TableHead className="bg-muted/40 border-r">注释</TableHead>
            {/* 目标字段（可编辑） */}
            <TableHead>目标字段名</TableHead>
            <TableHead>类型</TableHead>
            <TableHead>注释</TableHead>
            <TableHead>长度</TableHead>
            <TableHead>可空</TableHead>
            <TableHead>索引</TableHead>
            <TableHead>字典</TableHead>
            <TableHead>外键</TableHead>
            <TableHead>状态</TableHead>
            <TableHead>优化理由</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sourceColumns.map((col) => {
            const entry = mappingBySource.get(col.column_name);
            const rowKey = `src-${col.column_name}`;
            const companions = companionsByParent.get(col.column_name) || [];

            if (!entry) {
              return (
                <TableRow key={rowKey} className="h-10">
                  <TableCell className="font-mono bg-muted/20">{col.column_name}</TableCell>
                  <TableCell className="font-mono text-xs bg-muted/20">{col.data_type}</TableCell>
                  <TableCell className="bg-muted/20">{col.is_primary_key ? 'PK' : ''}</TableCell>
                  <TableCell className="text-muted-foreground text-xs max-w-[120px] truncate bg-muted/20 border-r" title={col.column_comment || ''}>{col.column_comment || ''}</TableCell>
                  <TableCell colSpan={10} className="p-1 text-center">
                    <span className="text-muted-foreground text-xs">未映射</span>
                  </TableCell>
                </TableRow>
              );
            }

            const { mapping: m, index: mappingIndex } = entry;

            return (
              <React.Fragment key={rowKey}>
                <TableRow className="h-10">
                  <TableCell className="font-mono bg-muted/20">{col.column_name}</TableCell>
                  <TableCell className="font-mono text-xs bg-muted/20">{col.data_type}</TableCell>
                  <TableCell className="bg-muted/20">{col.is_primary_key ? 'PK' : ''}</TableCell>
                  <TableCell className="text-muted-foreground text-xs max-w-[120px] truncate bg-muted/20 border-r" title={col.column_comment || ''}>{col.column_comment || ''}</TableCell>
                  {renderTargetColumns(m, mappingIndex, rowKey)}
                </TableRow>
                {renderDictPanel(m, mappingIndex, rowKey)}
                {renderFkPanel(m, mappingIndex, rowKey)}

                {/* FK 伴生列渲染（紧跟父列之后） */}
                {companions.map(({ mapping: cm, index: ci }) => (
                  <TableRow key={`companion-${cm.fk_companion_suffix}`} className="h-10 bg-blue-50/30">
                    <TableCell className="bg-muted/10 pl-6" colSpan={4}>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs text-[var(--color-accent-blue)] border-[var(--color-accent-blue)]">
                          FK 伴生
                        </Badge>
                        <span className="text-xs text-muted-foreground">← {col.column_name}</span>
                      </div>
                    </TableCell>
                    {renderTargetColumns(cm, ci, `companion-${cm.fk_companion_suffix}`, true)}
                  </TableRow>
                ))}
              </React.Fragment>
            );
          })}

          {/* 孤儿映射 */}
          {orphanMappings.map(({ mapping: m, index: mappingIndex }) => {
            const rowKey = `orphan-${m.source_column_name}`;
            return (
              <React.Fragment key={rowKey}>
                <TableRow className="h-10">
                  <TableCell className="bg-muted/20" colSpan={4}>
                    <Badge variant="outline" className="text-[var(--color-warning)]">
                      源字段已移除: {m.source_column_name}
                    </Badge>
                  </TableCell>
                  {renderTargetColumns(m, mappingIndex, rowKey)}
                </TableRow>
                {renderDictPanel(m, mappingIndex, rowKey)}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
